/**
 * FAQ Page - Alpine.js Components
 */

// Dictionnaire de synonymes pour la recherche FAQ
const FAQ_SYNONYMS = {
    // Commandes
    'recevoir': ['demander', 'obtenir', 'avoir'],
    'demander': ['recevoir', 'obtenir', 'avoir'],
    'obtenir': ['recevoir', 'demander', 'avoir'],
    'annuler': ['supprimer', 'résilier', 'arrêter'],
    'supprimer': ['annuler', 'effacer', 'retirer'],
    'modifier': ['changer', 'éditer', 'corriger', 'mettre à jour'],
    'changer': ['modifier', 'éditer', 'corriger'],
    // Paiement
    'payer': ['régler', 'acheter'],
    'régler': ['payer', 'acheter'],
    'prix': ['tarif', 'coût', 'montant'],
    'tarif': ['prix', 'coût', 'montant'],
    // Livraison
    'livraison': ['expédition', 'envoi', 'réception'],
    'expédition': ['livraison', 'envoi'],
    'délai': ['durée', 'temps', 'attente'],
    // Compte
    'compte': ['profil', 'espace'],
    'mot de passe': ['identifiant', 'connexion'],
    'connexion': ['identification', 'login'],
    // Produits
    'produit': ['article', 'référence'],
    'article': ['produit', 'référence'],
    // Actions
    'retourner': ['renvoyer', 'échanger'],
    'renvoyer': ['retourner', 'échanger'],
    'contacter': ['joindre', 'appeler', 'écrire'],
};

// Composant FAQ Search
function faqSearchComponent() {
    return {
        search: '',
        categories: [],
        filteredCategories: [],
        fuse: null,
        allFaqs: [],
        minSearchLength: 3,

        init() {
            this.categories = window.faqData || [];
            this.filteredCategories = [...this.categories];
            this.initFuseSearch();
        },

        // Initialise Fuse.js pour la recherche floue
        initFuseSearch() {
            // Créer une liste plate de toutes les FAQs avec leur catégorie
            this.allFaqs = [];
            this.categories.forEach(category => {
                category.faqs.forEach(faq => {
                    this.allFaqs.push({
                        ...faq,
                        categoryId: category.id,
                        categoryName: category.name,
                        categorySlug: category.slug,
                        searchText: [
                            faq.question || '',
                            faq.short_question || '',
                            category.name
                        ].join(' ')
                    });
                });
            });

            // Configurer Fuse.js
            if (typeof Fuse !== 'undefined') {
                this.fuse = new Fuse(this.allFaqs, {
                    keys: ['question', 'short_question', 'categoryName'],
                    threshold: 0.4, // 0 = exact, 1 = tout match
                    distance: 100,
                    includeScore: true,
                    minMatchCharLength: 2,
                });
            }
        },

        // Récupère les synonymes d'un mot
        getSynonyms(word) {
            const synonyms = [word];
            if (FAQ_SYNONYMS[word]) {
                synonyms.push(...FAQ_SYNONYMS[word]);
            }
            // Chercher aussi si le mot est un synonyme d'un autre
            Object.entries(FAQ_SYNONYMS).forEach(([key, values]) => {
                if (values.includes(word) && !synonyms.includes(key)) {
                    synonyms.push(key);
                }
            });
            return synonyms;
        },

        // Étend les mots de recherche avec leurs synonymes
        expandWithSynonyms(searchWords) {
            const expanded = new Set();
            searchWords.forEach(word => {
                this.getSynonyms(word).forEach(syn => expanded.add(syn));
            });
            return Array.from(expanded);
        },

        // Vérifie si au moins un mot (ou synonyme) est présent
        matchesWithSynonyms(text, searchWords) {
            const expandedWords = this.expandWithSynonyms(searchWords);
            return expandedWords.some(word => text.includes(word));
        },

        // Vérifie si tous les mots de recherche (avec synonymes) sont présents
        matchesAllWordsWithSynonyms(text, searchWords) {
            return searchWords.every(word => {
                const synonyms = this.getSynonyms(word);
                return synonyms.some(syn => text.includes(syn));
            });
        },

        filterCategories() {
            const searchTerm = this.search.toLowerCase().trim();

            if (searchTerm.length < this.minSearchLength) {
                this.filteredCategories = [...this.categories];
                return;
            }

            // Séparer en mots individuels (min 2 caractères par mot)
            const searchWords = searchTerm.split(/\s+/).filter(word => word.length >= 2);

            if (searchWords.length === 0) {
                this.filteredCategories = [...this.categories];
                return;
            }

            // Résultats par synonymes
            const synonymResults = new Map();

            this.categories.forEach(category => {
                const categoryNameMatches = this.matchesAllWordsWithSynonyms(category.name.toLowerCase(), searchWords);

                const matchingFaqs = category.faqs.filter(faq => {
                    const question = (faq.question || '').toLowerCase();
                    const shortQuestion = (faq.short_question || '').toLowerCase();
                    const combined = question + ' ' + shortQuestion;
                    return this.matchesAllWordsWithSynonyms(combined, searchWords);
                });

                if (categoryNameMatches || matchingFaqs.length > 0) {
                    synonymResults.set(category.id, {
                        ...category,
                        faqs: categoryNameMatches ? [...category.faqs] : matchingFaqs
                    });
                }
            });

            // Résultats Fuse.js (recherche floue)
            if (this.fuse) {
                const fuseResults = this.fuse.search(searchTerm);
                fuseResults.forEach(result => {
                    const faq = result.item;
                    // Ne garder que les résultats avec un score correct (< 0.5)
                    if (result.score < 0.5) {
                        const category = this.categories.find(c => c.id === faq.categoryId);
                        if (category && !synonymResults.has(category.id)) {
                            // Ajouter la catégorie si pas déjà présente
                            synonymResults.set(category.id, {
                                ...category,
                                faqs: category.faqs.filter(f =>
                                    fuseResults.some(r => r.item.id === f.id && r.score < 0.5)
                                )
                            });
                        } else if (category && synonymResults.has(category.id)) {
                            // Ajouter la FAQ si la catégorie existe déjà
                            const existingCategory = synonymResults.get(category.id);
                            if (!existingCategory.faqs.some(f => f.id === faq.id)) {
                                existingCategory.faqs.push(faq);
                            }
                        }
                    }
                });
            }

            this.filteredCategories = Array.from(synonymResults.values());
        },

        clearSearch() {
            this.search = '';
            this.filteredCategories = [...this.categories];
        },

        get isSearching() {
            return this.search.length >= this.minSearchLength;
        },

        get hasNoResults() {
            return this.isSearching && this.filteredCategories.length === 0;
        }
    };
}

// Debug : afficher les synonymes disponibles
window.faqSynonyms = FAQ_SYNONYMS;

// Exposer sur window pour x-data="faqSearch()"
window.faqSearch = faqSearchComponent;

// Aussi enregistrer via Alpine.data si Alpine est disponible
document.addEventListener('alpine:init', () => {
    Alpine.data('faqSearch', faqSearchComponent);
});

// Ouvrir automatiquement la FAQ si hash dans l'URL (page catégorie)
document.addEventListener('DOMContentLoaded', function() {
    const hash = window.location.hash;
    if (hash && hash.startsWith('#faq-')) {
        const faqId = parseInt(hash.replace('#faq-', ''));
        if (faqId) {
            // Attendre qu'Alpine soit initialisé
            setTimeout(() => {
                const accordion = document.querySelector('.faq-accordion');
                if (accordion && accordion._x_dataStack) {
                    accordion._x_dataStack[0].openFaq = faqId;
                }

                // Scroll vers l'élément
                const element = document.getElementById('faq-' + faqId);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
        }
    }
});
